import React from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert } from '@mui/material';
import { SentimentScoreLabels, SentimentScoreColors, FlowSignalTypeLabels, FlowSignalTypeColors, OptionTypeLabels, OptionTypeColors, AlertConditionLabels, AlertStatusLabels, AlertStatusColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

const CommunityTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const socialSentiments = data.social_sentiments || [];
    const optionsFlow = data.options_flow || [];
    const alerts = data.alerts || [];
    const newsSentiments = data.news_sentiments || [];

    return (
        <Box>
            {/* Social Sentiments */}
            <SectionTitle>Social Sentiments</SectionTitle>
            {socialSentiments.length === 0 ? <EmptyState message="No social sentiment data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Platform</TableCell>
                                <TableCell align="right">Mentions</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell align="right">Bullish Ratio</TableCell>
                                <TableCell>Analysis Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {socialSentiments.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.platform || '-'}</TableCell>
                                    <TableCell align="right">{formatNumber(row.mention_count)}</TableCell>
                                    <TableCell>
                                        <Chip label={SentimentScoreLabels[row.sentiment] || row.sentiment || '-'} size="small" color={SentimentScoreColors[row.sentiment] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPercent(row.bullish_ratio)}</TableCell>
                                    <TableCell>{formatDate(row.analysis_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Options Flow */}
            <SectionTitle>Options Flow</SectionTitle>
            {optionsFlow.length === 0 ? <EmptyState message="No options flow data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Flow Signal</TableCell>
                                <TableCell>Option Type</TableCell>
                                <TableCell align="right">Strike</TableCell>
                                <TableCell>Expiration</TableCell>
                                <TableCell align="right">Volume</TableCell>
                                <TableCell align="right">Premium</TableCell>
                                <TableCell>Direction</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {optionsFlow.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={FlowSignalTypeLabels[row.flow_signal_type] || row.flow_signal_type || '-'} size="small" color={FlowSignalTypeColors[row.flow_signal_type] || 'default'} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={OptionTypeLabels[row.option_type] || row.option_type || '-'} size="small" color={OptionTypeColors[row.option_type] || 'default'} />
                                    </TableCell>
                                    <TableCell align="right">{formatPrice(row.strike_price)}</TableCell>
                                    <TableCell>{formatDate(row.expiration_date)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.volume)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.premium_total)}</TableCell>
                                    <TableCell><SignalChip signal={row.implied_direction} /></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Price Alerts */}
            <SectionTitle>Price Alerts</SectionTitle>
            {alerts.length === 0 ? <EmptyState message="No price alerts" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Condition</TableCell>
                                <TableCell align="right">Target Price</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {alerts.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{AlertConditionLabels[row.condition] || row.condition}</TableCell>
                                    <TableCell align="right">{formatPrice(row.target_price)}</TableCell>
                                    <TableCell>
                                        <Chip label={AlertStatusLabels[row.status] || row.status} size="small" color={AlertStatusColors[row.status] || 'default'} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Recent News */}
            <SectionTitle>Recent News</SectionTitle>
            {newsSentiments.length === 0 ? <EmptyState message="No recent news" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Headline</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>Sentiment</TableCell>
                                <TableCell>Published</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {newsSentiments.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.headline || '-'}
                                    </TableCell>
                                    <TableCell>{row.source_name || '-'}</TableCell>
                                    <TableCell>
                                        <Chip label={SentimentScoreLabels[row.sentiment] || row.sentiment || '-'} size="small" color={SentimentScoreColors[row.sentiment] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.published_at)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default CommunityTab;
